import { DESKTOP_MIN_WIDTH, OBJECT_DEFAULT } from 'constants';
import {
  FORM_ADD_PAYTO_MAX_AMOUNT_FIELD,
  FORM_ADD_PAYTO_MAX_AMOUNT_NAME,
  isPayToLimitNullEquivalent,
  PAY_BY_PAYTO_AMOUNT_MAX_LIMIT,
} from 'features/to/const';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import { format } from 'helpers';
import useAddTransaction from 'hooks/form-add-transaction/useAddTransaction';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useLang from 'hooks/useLang';
import usePage from 'hooks/usePage';
import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';

const withCheckMaxAmount = (Component) => {
  const CheckMaxAmountComponent = (props) => {
    const {
      information,
      setInformation,
      validation,
      setValidation,
      errorStyles = OBJECT_DEFAULT,
      // DEV-1401: only ever passed by EditPaytoAccountAgreementModal, which
      // owns the Amend hard-stop/uneditable precedence (rules 1 and 2 --
      // rule 2's totalPay sub-condition included). When set, it is the sole
      // authority over this field's validation message: skips this HOC's
      // own maxLimit/minLimit checks entirely for that render so a
      // maxAmount-only-triggered re-run of this effect (e.g. totalToPay
      // changing while the disabled dropdown's own value is untouched)
      // can't silently clear or override a hard-stop/uneditable message the
      // parent has decided should be showing.
      amendForcedValidationMessage,
      // DEV-1401 Task 2A/2B: only ever passed by EditPaytoAccountAgreementModal,
      // true for the "selected dropdown maxAmount < totalPay, totalPay <=
      // amendPayToLimit" row (a higher option could still cover totalPay).
      // Deliberately excluded from amendForcedValidationMessage (see the
      // modal's own comment) so the field stays editable and its message
      // renders via keyError instead of the below-dropdown warning -- this
      // prop lets this HOC write that inline message directly, ahead of
      // (and instead of) the totalPay-vs-amendPayToLimit branch below.
      isAmendLimitValidation,
      // DEV-1401 Task 2B: sibling of isAmendLimitValidation -- true for
      // "selected dropdown maxAmount < totalPay, totalPay > amendPayToLimit"
      // (no selectable option, up to amendPayToLimit, could ever cover
      // totalPay). Also excluded from amendForcedValidationMessage and
      // editable/disabled identically to isAmendLimitValidation; only the
      // message text differs between the two.
      isAmendBankLimitValidation,
      // DEV-1401: only ever passed by AddPaytoAccountModal (Create flow).
      // Guards the totalPay-vs-amendPayToLimit branch below, which must
      // never fire for Create -- see that branch's comment for why
      // information.amendPayToLimit can be non-null-equivalent there too.
      isCreateFlowMaxAmountCheck,
    } = props || {};

    const { t } = useLang();
    const { isAccountAgreementEditing, isPaytoMaxAmountEdited } =
      usePaytoStore();
    const { isAddTransactionPage } = usePage();
    const { getTotalToPay } = useAddTransaction();
    const { getTotalPayAmount } = useFxFormAddTransactionStore();

    const maxAmount = Number(information?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME]);
    const totalToPay = isAddTransactionPage
      ? getTotalToPay()
      : getTotalPayAmount();
    // DEV-1401: normally only present on Amend-flow information
    // (accountAgreementInformation, via payloadTransformFromPaytoAccountAgreement).
    // Create-flow information CAN also carry this field via a documented,
    // separate path (AddPaytoAccountModal re-populating paytoAccountInformation
    // from an existing agreement, via withAddPaytoAccountsAgreement.jsx) --
    // isCreateFlowMaxAmountCheck below guards against that leaking into the
    // branch that reads this value, rather than relying on the field being
    // absent in Create.
    const amendPayToLimit = information?.amendPayToLimit;

    const { maxLimit, minLimit } = {
      maxLimit: PAY_BY_PAYTO_AMOUNT_MAX_LIMIT,
      minLimit: totalToPay,
    };

    const onCheckMaxAmountValidation = (maxAmount) => {
      let newValidation = { ...validation };

      // DEV-1401: parent-owned Amend precedence (rules 1 and 2) takes
      // absolute priority over this HOC's own checks below -- see the
      // amendForcedValidationMessage prop comment above.
      if (amendForcedValidationMessage) {
        newValidation = {
          ...newValidation,
          [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: amendForcedValidationMessage,
        };
        setValidation(newValidation);
        return;
      }

      // DEV-1401 Task 2A/2B: takes priority over the totalPay-vs-amendPayToLimit
      // branch immediately below, which would otherwise also match this row
      // and overwrite this message with the old forced-message text on every
      // re-run of this effect.
      if (isAmendLimitValidation || isAmendBankLimitValidation) {
        newValidation = {
          ...newValidation,
          [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: t(
            isAmendBankLimitValidation
              ? 'form_payto_bank_limit_cannot_pay_validation'
              : 'form_payto_limit_cannot_pay_validation'
          ),
        };
        setValidation(newValidation);
        return;
      }

      // DEV-1401 Amend precedence rule 2 (uneditable, not hard-stop):
      // totalPay > amendPayToLimit blocks every selectable maxAmount option,
      // regardless of which one is chosen -- extends this existing
      // validation gate rather than a separate one. Do NOT compare against
      // createPayToLimit here: that condition was removed from scope by
      // client correction and is a Create-flow-only concept.
      // isCreateFlowMaxAmountCheck excludes Create entirely: this branch is
      // Amend-only (it exists for an amendPayToLimit that isn't positive
      // enough for EditPaytoAccountAgreementModal's own equivalent check to
      // have already handled it via amendForcedValidationMessage above).
      if (
        !isCreateFlowMaxAmountCheck &&
        !isPayToLimitNullEquivalent(amendPayToLimit) &&
        Number(totalToPay) > Number(amendPayToLimit)
      ) {
        newValidation = {
          ...newValidation,
          [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: t(
            'label_payto_amend_unable_increase_limit_per_transaction_desc'
          ),
        };
        setValidation(newValidation);
        return;
      }

      if ((maxLimit && !minLimit) || (maxLimit && minLimit)) {
        if (maxAmount > maxLimit) {
          newValidation = {
            ...newValidation,
            [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: t(
              FORM_ADD_PAYTO_MAX_AMOUNT_FIELD.msgMaxLimited,
              {
                max: format.toAmountStr(maxLimit),
              }
            ),
          };
          setValidation(newValidation);
          return;
        }
      }

      if ((!maxLimit && minLimit) || (maxLimit && minLimit)) {
        if (maxAmount < minLimit) {
          newValidation = {
            ...newValidation,
            [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: t(
              FORM_ADD_PAYTO_MAX_AMOUNT_FIELD.msgMinLimited,
              {
                min: format.toAmountStr(minLimit),
              }
            ),
          };
          setValidation(newValidation);
          return;
        }
      }

      if (maxAmount <= maxLimit) {
        newValidation = {
          ...newValidation,
          [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: '',
        };
        setValidation(newValidation);
        return;
      }

      setValidation(newValidation);
      return;
    };

    useEffect(() => {
      onCheckMaxAmountValidation(Number(maxAmount));
    }, [
      maxAmount,
      isAccountAgreementEditing,
      isPaytoMaxAmountEdited,
      amendPayToLimit,
      amendForcedValidationMessage,
      isAmendLimitValidation,
      isAmendBankLimitValidation,
    ]);

    const getMessageValidation = (validation) => {
      return t(validation?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME]);
    };

    const newProps = {
      ...props,
    };
    return (
      <>
        <Component {...newProps} />
        {/* DEV-1401 Task 2A/2B: also excluded here -- isAmendLimitValidation/
            isAmendBankLimitValidation's message already renders via
            CustomMaxAmountDropdown's own keyError (see that component's
            identical amendForcedValidationMessage-gated exclusion), so this
            second, differently-styled feedback node must not render the same
            validation[field] text a second time. */}
        {!amendForcedValidationMessage &&
          !isAmendLimitValidation &&
          !isAmendBankLimitValidation && (
          <FormControlFeedback
            hidden={false}
            type="invalid"
            style={errorStyles || {}}
          >
            {getMessageValidation(validation)}
          </FormControlFeedback>
        )}
      </>
    );
  };
  return CheckMaxAmountComponent;
};

const FormControlFeedback = styled(Form.Control.Feedback)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  color: var(--ds-c-red);
  position: absolute;
  margin-top: 4px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;

export default withCheckMaxAmount;
